/**
 * Formats a file size in bytes to a human-readable string.
 * Automatically selects the appropriate unit (Bytes, KB, MB, GB, TB).
 *
 * @param bytes - The file size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "1.45 MB" or "3.2 GB"
 *
 * @example
 * ```typescript
 * formatFileSize(0);           // "0 Bytes"
 * formatFileSize(1024);        // "1 KB"
 * formatFileSize(1536);        // "1.5 KB"
 * formatFileSize(1048576);     // "1 MB"
 * formatFileSize(1234567);     // "1.18 MB"
 * formatFileSize(1234567890);  // "1.15 GB"
 * ```
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes == null || Number.isNaN(bytes) || bytes < 0) return "";
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Ensure we don't exceed our sizes array
  const sizeIndex = Math.min(i, sizes.length - 1);

  return `${parseFloat((bytes / Math.pow(k, sizeIndex)).toFixed(decimals))} ${sizes[sizeIndex]}`;
}
